import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { CreditCard, AlertCircle, Tag } from 'lucide-react';
import AuthFloatingControls from './AuthFloatingControls.jsx';

const defaultPricing = {
  amount_cents: 150000,
  monthly_display_cents: 12500,
  currency: 'SAR',
  duration_days: 365
};

const centsToMoney = (cents) => (Number(cents || 0) / 100).toFixed(2).replace(/\.00$/, '');

const PaymentRequired = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { subscriptionStatus } = useAuth();
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [pricing, setPricing] = useState(defaultPricing);
  const [promoCodeInput, setPromoCodeInput] = useState('');
  const [promoApplied, setPromoApplied] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const isExpired = subscriptionStatus === 'expired';

  const display = useMemo(() => {
    const base = pricing.amount_cents;
    const sale = pricing.sale;
    if (!promoApplied?.valid) {
      const saleFinal = sale?.discounted_amount_cents ?? base;
      const saleDiscount = sale?.savings_cents ?? 0;
      return {
        original: base,
        final: saleFinal,
        monthly: Math.round(saleFinal / 12),
        discount: saleDiscount,
        sale
      };
    }
    const final = promoApplied.final_amount_cents;
    return {
      original: promoApplied.original_amount_cents,
      final,
      monthly: Math.round(final / 12),
      discount: promoApplied.discount_amount_cents,
      sale
    };
  }, [pricing, promoApplied]);

  useEffect(() => {
    const loadPricing = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
        const res = await fetch(`${API_BASE_URL}/payment/pricing`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) return;
        const data = await res.json();
        setPricing((prev) => ({ ...prev, ...data }));
      } catch {
        // keep defaults
      }
    };
    loadPricing();
  }, []);

  const handleApplyPromo = async () => {
    setPaymentError('');
    setPromoLoading(true);
    try {
      const code = promoCodeInput.trim();
      if (!code) {
        setPromoApplied(null);
        return;
      }
      const token = localStorage.getItem('token');
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_BASE_URL}/payment/validate-promo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ code })
      });
      const data = await response.json();
      if (!data.valid) {
        setPromoApplied({ valid: false, reason: data.reason || 'invalid' });
        return;
      }
      setPromoApplied({ ...data, valid: true });
    } catch (err) {
      setPromoApplied({ valid: false, reason: err.message || 'invalid' });
    } finally {
      setPromoLoading(false);
    }
  };

  const handlePayNow = async () => {
    setPaymentError('');
    setPaymentLoading(true);
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
      const token = localStorage.getItem('token');
      if (!token) {
        setPaymentError(t('auth.sessionExpired', { defaultValue: 'Session expired. Please log in again.' }));
        setPaymentLoading(false);
        navigate('/login', { replace: true });
        return;
      }
      const response = await fetch(`${API_BASE_URL}/payment/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          promoCode: promoApplied?.valid ? promoCodeInput.trim() : undefined
        })
      });
      const data = await response.json();
      if (!response.ok) {
        setPaymentError(data.message || data.detail || 'Failed to start payment');
        setPaymentLoading(false);
        return;
      }
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
        return;
      }
      setPaymentError('Payment link not available');
    } catch (err) {
      setPaymentError(err.message || 'Network error');
    } finally {
      setPaymentLoading(false);
    }
  };

  return (
    <div className={`min-h-screen relative overflow-hidden ${i18n.language === 'ar' ? 'rtl' : 'ltr'}`}>
      <AuthFloatingControls />
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-yellow-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000" />
        <div className="absolute top-40 left-40 w-80 h-80 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000" />
      </div>
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="glass-card">
            <div className={`py-8 text-center ${i18n.language === 'ar' ? 'font-arabic' : ''}`}>
              <CreditCard className="h-20 w-20 mx-auto mb-6" style={{ color: 'var(--accent-primary)' }} />
              <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                {t('auth.subscriptionRequired', { defaultValue: 'Subscription required' })}
              </h1>
              <p className="text-lg font-semibold mb-1" style={{ color: 'var(--primary)' }}>
                {centsToMoney(display.monthly)} {pricing.currency}/month
              </p>
              <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
                {t('auth.subscriptionAnnualBilled', {
                  amount: centsToMoney(display.final),
                  currency: pricing.currency,
                  defaultValue: `You pay ${centsToMoney(display.final)} ${pricing.currency} once for 1 year.`
                })}
              </p>
              {!promoApplied?.valid && display.sale && (
                <div
                  className={`mb-4 rounded-xl p-4 ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}
                  style={{
                    background: 'rgba(34, 197, 94, 0.08)',
                    border: '1px solid rgba(34, 197, 94, 0.25)'
                  }}
                >
                  <p className="text-xs font-semibold mb-1" style={{ color: 'var(--success-color)' }}>
                    {t('auth.activeSaleCampaign', { defaultValue: 'Active sale campaign' })}
                  </p>
                  <p className="text-sm font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>
                    {display.sale.name}
                  </p>
                  <p className="text-xs mb-2" style={{ color: 'var(--text-secondary)' }}>
                    {t('auth.saleSavingsYearly', {
                      amount: centsToMoney(display.sale.savings_cents),
                      currency: pricing.currency,
                      defaultValue: `Save ${centsToMoney(display.sale.savings_cents)} ${pricing.currency} on yearly subscription`
                    })}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                    <span style={{ textDecoration: 'line-through' }}>
                      {centsToMoney(pricing.amount_cents)} {pricing.currency}
                    </span>{' '}
                    {' -> '}
                    <span className="font-semibold" style={{ color: 'var(--success-color)' }}>
                      {centsToMoney(display.sale.discounted_amount_cents)} {pricing.currency}
                    </span>{' '}
                    {t('auth.forOneYear', { defaultValue: 'for 1 year' })}
                  </p>
                </div>
              )}
              {promoApplied?.valid && display.sale && (
                <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
                  {t('auth.promoOverridesSale', { defaultValue: 'Promo code overrides the active sale.' })}
                </p>
              )}
              <div className={`rounded-xl p-3 mb-4 ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`} style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}>
                <div className={`flex items-center gap-2 mb-2 ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`}>
                  <Tag className="h-4 w-4" />
                  <span className="text-sm font-semibold">{t('auth.havePromo', { defaultValue: 'Have a promo code?' })}</span>
                </div>
                <div className={`flex gap-2 ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`}>
                  <input
                    value={promoCodeInput}
                    onChange={(e) => setPromoCodeInput(e.target.value.toUpperCase())}
                    placeholder={t('auth.enterPromo', { defaultValue: 'Enter promo code' })}
                    className={`flex-1 px-3 py-2 rounded-lg border bg-background ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}
                    style={{ borderColor: 'var(--border)' }}
                  />
                  <button
                    onClick={handleApplyPromo}
                    disabled={promoLoading}
                    className="glass-button-outline px-3 py-2 text-sm"
                  >
                    {promoLoading ? t('auth.loading', { defaultValue: 'Loading...' }) : t('auth.apply', { defaultValue: 'Apply' })}
                  </button>
                </div>
                {promoApplied?.valid && (
                  <p className="text-xs mt-2" style={{ color: 'var(--success-color)' }}>
                    {t('auth.promoApplied', {
                      amount: centsToMoney(display.discount),
                      currency: pricing.currency,
                      defaultValue: `Promo applied: save ${centsToMoney(display.discount)} ${pricing.currency}`
                    })}
                  </p>
                )}
                {promoApplied && promoApplied.valid === false && (
                  <p className="text-xs mt-2" style={{ color: 'var(--error-color)' }}>
                    {t('auth.invalidPromo', { defaultValue: 'Invalid or expired promo code.' })}
                  </p>
                )}
              </div>

              {display.discount > 0 && (
                <p className="text-xs mb-4" style={{ color: 'var(--text-secondary)' }}>
                  <span style={{ textDecoration: 'line-through' }}>
                    {centsToMoney(display.original)} {pricing.currency}
                  </span>{' '}
                  →{' '}
                  <span className="font-semibold">
                    {centsToMoney(display.final)} {pricing.currency}
                  </span>
                </p>
              )}

              {paymentError && (
                <div className={`mb-4 p-4 rounded-xl flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-3' : 'space-x-3'}`} style={{
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.2)',
                  color: 'var(--error-color)'
                }}>
                  <AlertCircle className="h-5 w-5 flex-shrink-0" />
                  <span className="text-sm">{paymentError}</span>
                </div>
              )}
              <button
                className="glass-button w-full py-3 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mb-4"
                onClick={handlePayNow}
                disabled={paymentLoading}
              >
                {paymentLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                    <span>{t('auth.loading', { defaultValue: 'Loading...' })}</span>
                  </>
                ) : (
                  <>
                    <CreditCard className="h-5 w-5" />
                    <span>{t('auth.payNow', { defaultValue: 'Pay now' })}</span>
                  </>
                )}
              </button>
              <button className="glass-button-outline w-full py-3 text-lg font-semibold" onClick={() => navigate('/login', { replace: true })}>
                {t('auth.goToLogin', { defaultValue: 'Go to login' })}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentRequired;
